import React, { useState, useEffect, useCallback } from "react";
import { FaCheckCircle, FaTrash, FaCheck, FaUserAlt, FaCalendarAlt, FaPagelines, FaClock, FaCalendarPlus, FaTree, FaMapMarkerAlt } from "react-icons/fa";
import api from "../../axios/api";
import { ToastContainer, toast } from 'react-toastify';
import "../../assets/NewFiles/Css/Dashboard.css";
import Select from 'react-select';
import Loader from "../common/Loader";
import Button from "../common/styled components/Button";
import { Box, Pagination } from "@mui/material";

const VerifyPosts = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("latest");
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 9
  });

  const rejectionOptions = [
    { value: "not_tree", label: "Not Tree" },
    { value: "blurry_image", label: "Blurry Image" },
    { value: "duplicate_image", label: "Duplicate Image" },
    { value: "dead_tree", label: "Dead Tree" },
    { value: "need_to_contact", label: "Need to contact" },
  ];

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/admin/getPosts", {
        params: {
          page: pagination.currentPage,
          pageSize: pagination.pageSize,
          search: searchTerm,
          filter: filter,
          sortOrder: sortOrder
        }
      });
      
      setPosts(response.data.images);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.totalPages,
        totalItems: response.data.totalItems
      }));
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to fetch posts");
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, searchTerm, filter, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPosts();
    }, 400); // Debounce searches

    return () => clearTimeout(timer);
  }, [fetchPosts]);

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilter = (e) => setFilter(e.target.value);
  
  const handleSortOrder = (e) => {
    setSortOrder(e.target.value);
    setPagination(prev => ({...prev, currentPage: 1})); // Reset to first page when changing sort
  };

  const handleVerify = async (id) => {
    setIsVerifying(true);
    try {
      await api.patch(`/api/admin/posts/${id}/verify`);
      toast.success("Verified");
      fetchPosts();
    } catch (error) {
      console.error("Error verifying post:", error);
      toast.error("Failed to verify post");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDelete = async (id) => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/admin/posts/${id}`);
      toast.error("Deleted");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReject = async (id, reason) => {
    setIsRejecting(true);
    try {
      await api.post(`/api/admin/posts/reject/${id}`, { Reason: reason.value });
      toast.error("Rejected");
      fetchPosts();
    } catch (error) {
      console.error("Error rejecting post:", error);
      toast.error("Failed to reject post");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleSelectPost = (id) => {
    setSelectedPosts((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((postId) => postId !== id)
        : [...prevSelected, id]
    );
  };

  const handleVerifySelected = async () => {
    setIsVerifying(true);
    try {
      await Promise.all(
        selectedPosts.map((id) => api.patch(`/api/admin/posts/${id}/verify`))
      );
      setSelectedPosts([]);
      toast.success("Selected posts verified");
      fetchPosts();
    } catch (error) {
      console.error("Error verifying selected posts:", error);
      toast.error("Failed to verify selected posts");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <ToastContainer />
      {isLoading && (
        <div className="loading-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Loader />
        </div>
      )}
      <div className={isLoading ? 'content-disabled' : ''} style={{
        pointerEvents: isLoading ? 'none' : 'auto',
        opacity: isLoading ? 0.6 : 1,
        transition: 'opacity 0.3s'
      }}>
        <h2>Verify Trees</h2>
        <div className="controls" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by Plant Name..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-bar"
            disabled={isLoading}
            style={{
              height: '32px',
              padding: '6px 12px',
            }}
          />
          <input
            type="text"
            placeholder="Filter by Planter Name..."
            value={filter}
            onChange={handleFilter}
            className="filter-input"
            disabled={isLoading}
            style={{
              height: '32px',
              padding: '6px 12px',
            }}
          />
          <select
            value={sortOrder}
            onChange={handleSortOrder}
            className="sort-select"
            disabled={isLoading}
            style={{
              height: '32px',
              padding: '4px 8px',
              fontSize: '14px',
            }}
          >
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        
        <div style={{ marginTop: '15px', marginBottom: '15px' }}>
          <Button
            onClick={handleVerifySelected}
            disabled={selectedPosts.length === 0 || isVerifying || isLoading}
          >
            <FaCheck />
            <span className="ml-2">Verify Selected ({selectedPosts.length})</span>
          </Button>
        </div>

        <div className="posts-grid">
          {posts.map((post) => (
            <div
              key={post.id}
              className={`post-card ${post.isVerified === 1 ? "verified" : ""}`}
            >
              <input
                type="checkbox"
                checked={selectedPosts.includes(post.id)}
                onChange={() => handleSelectPost(post.id)}
                className="select-checkbox"
                disabled={isLoading}
              />
              <img
                src={post.treeImageUrl}
                alt={post.imageName}
                className="post-image"
              />
              <div style={{ textAlign: 'left', padding: '10px', lineHeight: '1.5' }}>
                <h3 title="Tree Name">{post.treeName}</h3>

                <p className="p-label" title="Tree Id"><FaTree style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /> {post.tree_id}</p>
                <br />
                <p className="p-label" title="Uploaded By"><FaUserAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />  {post.full_name}</p>
                <br />
                <p className="p-label" title="Uploaded On"><FaCalendarPlus style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />  {new Date(post.createdAt).toLocaleDateString()}</p>
                <br />
                <p className="p-label" title="Planted On"><FaCalendarAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} />  {new Date(post.plantedOn).toLocaleDateString()}</p>
                <br />
                <p className="p-label" title="Species"><FaPagelines style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /> {post.speciesLabel}</p>
                <br />
                <p className="p-label" title="Age"><FaClock style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /> {post.age} year(s)</p>
                <br />
                <p className="post-address" title="Location"><FaMapMarkerAlt style={{ display: "inline-flex", marginBottom: '2px' }} color={'#002D62'} size={14} /> {post.address}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
                <Button
                  onClick={() => handleVerify(post.id)}
                  disabled={post.isVerified === 1 || isVerifying || isLoading}
                >
                  <FaCheckCircle style={{ marginRight: '5px' }} /> {post.isVerified === 1 ? "Verified" : "Verify"}
                </Button>

                <Button
                  onClick={() => handleDelete(post.id)}
                  variant="cancel"
                  disabled={isDeleting || isLoading}
                >
                  <FaTrash style={{ marginRight: '5px' }} /> Delete
                </Button>
              </div>

              <div className="flex items-center gap-2 w-full" style={{ padding: '0 10px', marginTop: '10px' }}>
                <div className="flex-1">
                  <Select
                    options={rejectionOptions}
                    onChange={(selectedOption) => handleReject(post.id, selectedOption)}
                    placeholder="Reject Post"
                    className="reject-select"
                    classNamePrefix="reject-select"
                    isDisabled={isRejecting || isLoading}
                    menuPlacement="top"
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: '100%',
                      }),
                      control: (base) => ({
                        ...base,
                        minHeight: '30px',
                        fontSize: '12px',
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 10,
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {posts.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              className="pagination-container"
              showFirstButton
              showLastButton
              disabled={isLoading}
            />
          </Box>
        )}
        
        {posts.length === 0 && !isLoading && (
          <div style={{ textAlign: 'center', padding: '30px', marginTop: '20px' }}>
            <p>No posts found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyPosts;